/**
 * @fileoverview HoverCard - Hover-triggered card popover
 *
 * Purpose: Display rich content on hover (like Twitter user cards)
 * Dependencies: Radix UI HoverCard primitives
 * Used by: User profiles, link previews, additional context
 *
 * Components exported:
 * - HoverCard: Root hover card container
 * - HoverCardTrigger: Element that triggers on hover
 * - HoverCardContent: Card content panel
 *
 * Features:
 * - Appears on hover
 * - Delay before showing/hiding
 * - Automatic positioning
 * - Accessible (ARIA attributes)
 * - Smooth animations
 * - Portal rendering
 *
 * @example
 * <HoverCard>
 *   <HoverCardTrigger asChild>
 *     <Button variant="link">@username</Button>
 *   </HoverCardTrigger>
 *   <HoverCardContent>
 *     <div className="space-y-2">
 *       <h4 className="text-sm font-semibold">@username</h4>
 *       <p className="text-sm text-muted-foreground">
 *         User bio and information
 *       </p>
 *     </div>
 *   </HoverCardContent>
 * </HoverCard>
 */

"use client";

import * as React from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";

import { cn } from "@/utils/misc";

const HoverCard = HoverCardPrimitive.Root;

const HoverCardTrigger = HoverCardPrimitive.Trigger;

const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => (
  <HoverCardPrimitive.Content
    ref={ref}
    align={align}
    sideOffset={sideOffset}
    className={cn(
      "z-50 w-64 origin-[--radix-hover-card-content-transform-origin] rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className,
    )}
    {...props}
  />
));
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName;

export { HoverCard, HoverCardTrigger, HoverCardContent };
